/**
 * Platform Templates manager.
 *
 * Platform admin uploads .formflo.json bundles, tags them with a category,
 * and toggles is_active. Any org_admin can then instantiate them.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    PackageIcon, Plus, Trash, PencilSimple, UploadSimple, Stack, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const blank = () => ({
    name: "", description: "", category: "General", icon: "", bundle: null,
});

export default function PlatformTemplatesPage() {
    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(blank());
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);
    const [search, setSearch] = useState("");
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const [t, c] = await Promise.all([
                api.get("/templates"), api.get("/templates/categories"),
            ]);
            setTemplates(t.data); setCategories(c.data);
        } catch (e) { toast.error(extractError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return templates;
        return templates.filter((t) =>
            t.name.toLowerCase().includes(q)
            || t.description?.toLowerCase().includes(q)
            || t.category?.toLowerCase().includes(q),
        );
    }, [templates, search]);

    const onPickFile = (e) => {
        const file = e.target.files?.[0]; e.target.value = "";
        if (!file) return;
        const reader = new FileReader();
        reader.onerror = () => toast.error("Could not read file");
        reader.onload = () => {
            try {
                const json = JSON.parse(reader.result);
                if (!json?.form?.form_code) throw new Error("Bundle missing form.form_code");
                setDraft({
                    ...draft,
                    name: draft.name || json.form.form_title || json.form.form_name || "Untitled template",
                    description: draft.description || json.form.description || "",
                    bundle: json,
                });
                toast.success("Bundle loaded");
            } catch (err) {
                toast.error(`Invalid bundle: ${err.message}`);
            }
        };
        reader.readAsText(file);
    };

    const openNew = () => { setDraft(blank()); setEditing(null); setOpen(true); };
    const openEdit = async (t) => {
        // load full record (with bundle) so admin can re-upload if they want
        try {
            const { data } = await api.get(`/templates/${t.id}`);
            setDraft({ name: data.name, description: data.description, category: data.category, icon: data.icon || "", bundle: data.bundle });
            setEditing(t);
            setOpen(true);
        } catch (e) { toast.error(extractError(e)); }
    };

    const save = async () => {
        if (!draft.name.trim()) { toast.error("Name is required"); return; }
        if (!editing && !draft.bundle) { toast.error("Upload a .formflo.json bundle"); return; }
        setBusy(true);
        try {
            if (editing) {
                const patch = { name: draft.name, description: draft.description, category: draft.category, icon: draft.icon || null };
                if (draft.bundle && draft.bundle !== editing.bundle) patch.bundle = draft.bundle;
                await api.patch(`/templates/${editing.id}`, patch);
                toast.success("Template updated");
            } else {
                await api.post("/templates", {
                    name: draft.name, description: draft.description,
                    category: draft.category || "General",
                    icon: draft.icon || null,
                    bundle: draft.bundle,
                });
                toast.success("Template created");
            }
            setOpen(false); await load();
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusy(false); }
    };

    const toggleActive = async (t) => {
        try { await api.patch(`/templates/${t.id}`, { is_active: !t.is_active }); await load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    const del = async (t) => {
        if (!confirm(`Delete template "${t.name}"?`)) return;
        try { await api.delete(`/templates/${t.id}`); toast.success("Deleted"); await load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    return (
        <PageContainer>
            <PageHeader
                overline="Platform"
                title="Templates"
                description="Curated form bundles available to every organization. Categorize for discoverability."
                actions={<Button onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-template-btn"><Plus size={16} className="mr-2" /> New template</Button>}
                testid="templates-header"
            />

            <div className="relative mb-4 max-w-md" data-testid="templates-search">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, category, description" className="pl-9 rounded-sm" data-testid="templates-search-input" />
            </div>

            {filtered.length === 0 ? (
                <EmptyState icon={Stack} title="No templates yet" description="Upload a .formflo.json file to share with all orgs." testid="templates-empty" />
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
                    {filtered.map((t) => (
                        <div key={t.id} className="brut-card p-5" data-testid={`template-card-${t.id}`}>
                            <div className="flex items-start justify-between mb-3">
                                <Badge variant="outline" className="rounded-sm font-mono text-[9px]">{t.category}</Badge>
                                <Switch checked={t.is_active} onCheckedChange={() => toggleActive(t)} data-testid={`template-active-${t.id}`} />
                            </div>
                            <h3 className="text-lg font-bold tracking-tight mb-1">{t.name}</h3>
                            {t.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{t.description}</p>}
                            <div className="flex items-center gap-3 text-[10px] font-mono text-muted-foreground mb-4">
                                <span>{t.sections_count} sections</span>
                                <span>·</span>
                                <span>{t.fields_count} fields</span>
                                {t.workflow_steps_count > 0 && <><span>·</span><span>{t.workflow_steps_count} workflow steps</span></>}
                            </div>
                            <div className="flex gap-2 pt-3 border-t border-border">
                                <Button variant="outline" size="sm" className="rounded-sm flex-1" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}><PencilSimple size={12} className="mr-1" /> Edit</Button>
                                <Button variant="outline" size="sm" className="rounded-sm text-destructive" onClick={() => del(t)} data-testid={`del-template-${t.id}`}><Trash size={12} /></Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm max-w-xl" data-testid="template-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? `Edit "${editing.name}"` : "New template"}</DialogTitle>
                        <DialogDescription>Org admins can instantiate this from the Forms page.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="label-overline">Name</Label>
                            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-2 rounded-sm" data-testid="template-name-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-overline">Category</Label>
                                <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                                    <SelectTrigger className="mt-2 rounded-sm" data-testid="template-category-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="label-overline">Icon (optional)</Label>
                                <Input value={draft.icon} onChange={(e) => setDraft({ ...draft, icon: e.target.value })} placeholder="e.g. UsersThree" className="mt-2 rounded-sm font-mono text-xs" data-testid="template-icon-input" />
                            </div>
                        </div>
                        <div>
                            <Label className="label-overline">Description</Label>
                            <Textarea rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="mt-2 rounded-sm" data-testid="template-desc-input" />
                        </div>
                        <div>
                            <Label className="label-overline">Bundle (.formflo.json)</Label>
                            <input ref={fileRef} type="file" accept=".json,application/json" onChange={onPickFile} className="hidden" data-testid="template-file-input" />
                            <div className="mt-2 flex items-center gap-2">
                                <Button type="button" variant="outline" className="rounded-sm" onClick={() => fileRef.current?.click()} data-testid="template-upload-btn">
                                    <UploadSimple size={14} className="mr-1" /> {draft.bundle ? "Replace bundle" : "Upload bundle"}
                                </Button>
                                {draft.bundle && (
                                    <span className="text-xs font-mono text-muted-foreground">
                                        {draft.bundle.form?.form_code} · {(draft.bundle.sections || []).length} sections · {(draft.bundle.fields || []).length} fields
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                        <Button onClick={save} disabled={busy} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-template-btn">
                            {busy ? "Saving…" : (editing ? "Save changes" : "Create template")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
